import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import FilterAltTwoToneIcon from '@mui/icons-material/FilterAltTwoTone';
import HistoryTwoToneIcon from '@mui/icons-material/HistoryTwoTone';
import PublicTwoToneIcon from '@mui/icons-material/PublicTwoTone';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  GlobalSearchCategory,
  GlobalSearchResults,
  SearchResultItem,
  SearchResultNavigationContext,
} from 'switch-shared/modules/features/global-search/global-search.dto';

import EntityIcon from '../../../components/Icons/EntityIcon';
import { useAdminUrlNavigation } from '../../../hooks/useAdminUrlNavigation';
import { useApplicationGroups } from '../../../hooks/useApplicationGroups';
import { useApplications } from '../../../hooks/useApplications';
import useEffectiveApplicationGroupId from '../../../hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from '../../../hooks/useEffectiveApplicationId';
import { useNavigateWithState } from '../../../hooks/useNavigateWithState';
import { useLazyGlobalSearchQuery } from '../../../services/switch-rest-api/global-search.service';
import { isAllSelection } from '../../../utils/selection';
import { userStorage } from '../../../utils/userStorage';
import { buildSearchResultNavigationTarget } from './searchResultNavigation';

const RECENT_ITEMS_KEY = 'search-recent-items';
const BOOKMARK_ITEMS_KEY = 'search-bookmark-items';
const MAX_RECENT_ITEMS = 5;
// Per-category cap within each results section (selected app / rest of group).
const SECTION_LIMIT = 5;

interface RecentItem {
  archived?: boolean;
  id: string;
  title: string;
  subtitle?: string;
  type: GlobalSearchCategory;
  metadata?: SearchResultNavigationContext;
  navigationContext?: SearchResultNavigationContext;
}

const SearchResultsPaper = styled(Paper)(
  ({ theme }) => `
  width: 500px;
  max-width: 90vw;
  max-height: 500px;
  overflow: auto;
  margin-top: ${theme.spacing(1)};
  color: ${theme.tokens.colors.cardForeground};
  box-shadow: ${theme.tokens.shadows.lg};
  border-radius: ${theme.tokens.borderRadius.md};
`
);

const GroupHeader = styled(Typography)(
  ({ theme }) => `
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${theme.palette.text.secondary};
  padding: ${theme.spacing(1, 2)};
  background-color: ${theme.palette.background.default};
`
);

interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  type: GlobalSearchCategory;
  navigationContext?: SearchResultNavigationContext;
  archived?: boolean;
}

interface GroupedResults {
  [key: string]: SearchResult[];
}

const getSearchResultNavigationContext = (
  navigationContext?: SearchResultNavigationContext | Record<string, unknown>
): SearchResultNavigationContext | undefined => {
  const applicationId =
    typeof navigationContext?.applicationId === 'string'
      ? navigationContext.applicationId
      : undefined;
  const applicationGroupId =
    typeof navigationContext?.applicationGroupId === 'string'
      ? navigationContext.applicationGroupId
      : undefined;
  const applicationGroupIds = Array.isArray(
    navigationContext?.applicationGroupIds
  )
    ? navigationContext.applicationGroupIds.filter(
        (groupId): groupId is string => typeof groupId === 'string'
      )
    : undefined;
  const allApplicationGroups =
    typeof navigationContext?.allApplicationGroups === 'boolean'
      ? navigationContext.allApplicationGroups
      : undefined;

  if (
    !applicationId &&
    !applicationGroupId &&
    !applicationGroupIds?.length &&
    allApplicationGroups === undefined
  ) {
    return undefined;
  }

  return {
    applicationId,
    applicationGroupId,
    applicationGroupIds,
    allApplicationGroups,
  };
};

// Category name mapping from backend to display
const CATEGORY_DISPLAY_NAMES: Record<GlobalSearchCategory, string> = {
  [GlobalSearchCategory.APPLICATION_GROUPS]: 'Application Groups',
  [GlobalSearchCategory.USERS]: 'Users',
  [GlobalSearchCategory.TARIFFS]: 'Tariffs',
  [GlobalSearchCategory.APPLICATIONS]: 'Applications',
  [GlobalSearchCategory.ACCOUNTS]: 'Accounts',
  [GlobalSearchCategory.UTILITIES]: 'Utilities',
  [GlobalSearchCategory.METER_POINTS]: 'Meter Points',
  [GlobalSearchCategory.ZONES]: 'Zones',
  [GlobalSearchCategory.METERS]: 'Meters',
  [GlobalSearchCategory.GATEWAYS]: 'Gateways',
  [GlobalSearchCategory.SENSORS]: 'Sensors',
};

// Transform API results to component format
const transformSearchResults = (
  apiResults: GlobalSearchResults
): GroupedResults => {
  const transformed: GroupedResults = {};

  Object.entries(apiResults).forEach(([key, items]) => {
    if (!items || !Array.isArray(items) || items.length === 0) {
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(CATEGORY_DISPLAY_NAMES, key)) {
      return;
    }

    const displayName = CATEGORY_DISPLAY_NAMES[key as GlobalSearchCategory];
    transformed[displayName] = (items as SearchResultItem[]).map((item) => ({
      id: item.id,
      title: item.title,
      subtitle: item.subtitle,
      type: item.type,
      archived: item.archived,
      navigationContext: getSearchResultNavigationContext(
        item.navigationContext || item.metadata
      ),
    }));
  });

  return transformed;
};

const Search = () => {
  /* Hooks & Use Effects */
  const theme = useTheme();
  const navigate = useNavigate();
  const { navigateWithApplicationContext, navigateWithGroupContext } =
    useNavigateWithState();
  const { isUrlNavigationEnabled } = useAdminUrlNavigation();

  // Scope search results to the currently selected group / application. "All"
  // selections (or none) leave the corresponding filter unset → global search.
  const selectedGroupId = useEffectiveApplicationGroupId();
  const selectedAppId = useEffectiveApplicationId();
  const scopedApplicationGroupId =
    selectedGroupId && !isAllSelection(selectedGroupId)
      ? selectedGroupId
      : undefined;
  const scopedApplicationId =
    selectedAppId && !isAllSelection(selectedAppId) ? selectedAppId : undefined;

  // Resolve the scope ids to human-readable names for the results header. Both
  // lists are already cached app-wide (used by the sidebar).
  const { applicationGroups } = useApplicationGroups();
  const { applications } = useApplications();
  const scopeGroupName = scopedApplicationGroupId
    ? applicationGroups?.find((group) => group._id === scopedApplicationGroupId)
        ?.name
    : undefined;
  const scopeAppName = scopedApplicationId
    ? applications?.find((app) => app._id === scopedApplicationId)?.name
    : undefined;
  // Results are always group-scoped (a selected application broadens to its
  // group), so the header shows the group; "all groups" when nothing is scoped.
  const scopeGroupLabel = scopedApplicationGroupId
    ? (scopeGroupName ?? 'selected group')
    : undefined;

  /* States */
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<GroupedResults>({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  const [bookmarkedItems, setBookmarkedItems] = useState<RecentItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listItemsRef = useRef<(HTMLElement | null)[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /* API Hooks */
  const [triggerSearch, { data: searchData, isFetching, isError }] =
    useLazyGlobalSearchQuery();

  /* Computed values */
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isDropdownActive = Boolean(anchorEl) || dialogOpen;
  const showRecents =
    isDropdownActive &&
    searchQuery.length === 0 &&
    (recentItems.length > 0 || bookmarkedItems.length > 0);
  const showResults = isDropdownActive && searchQuery.length > 0;
  const open = isSmallScreen ? dialogOpen : showRecents || showResults;

  /* API Calls */

  /* Functions & Functional Components */
  const loadRecentItems = () => {
    try {
      const stored = userStorage.getItem(RECENT_ITEMS_KEY);
      if (stored) {
        setRecentItems(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load recent items:', error);
    }
  };

  const loadBookmarkedItems = () => {
    try {
      const stored = userStorage.getItem(BOOKMARK_ITEMS_KEY);
      if (stored) {
        setBookmarkedItems(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load bookmarked items:', error);
    }
  };

  const upsertBookmarkedItem = useCallback((item: RecentItem) => {
    setBookmarkedItems((prev) => {
      const index = prev.findIndex((i) => i.id === item.id);
      if (index === -1) return prev;
      const updated = [...prev];
      updated[index] = { ...updated[index], ...item };
      try {
        userStorage.setItem(BOOKMARK_ITEMS_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Failed to update bookmarked items:', error);
      }
      return updated;
    });
  }, []);

  const saveRecentItem = useCallback(
    (item: RecentItem) => {
      if (!item.id) return;

      const updated = [
        item,
        ...recentItems.filter((i) => i.id !== item.id),
      ].slice(0, MAX_RECENT_ITEMS);

      setRecentItems(updated);
      try {
        userStorage.setItem(RECENT_ITEMS_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Failed to save recent item:', error);
      }

      upsertBookmarkedItem(item);
    },
    [recentItems, upsertBookmarkedItem]
  );

  const clearRecentItems = useCallback(() => {
    setRecentItems([]);
    try {
      userStorage.removeItem(RECENT_ITEMS_KEY);
    } catch (error) {
      console.error('Failed to clear recent items:', error);
    }
  }, []);

  const removeRecentItem = useCallback(
    (idToRemove: string) => {
      const updated = recentItems.filter((item) => item.id !== idToRemove);
      setRecentItems(updated);
      try {
        if (updated.length > 0) {
          userStorage.setItem(RECENT_ITEMS_KEY, JSON.stringify(updated));
        } else {
          userStorage.removeItem(RECENT_ITEMS_KEY);
        }
      } catch (error) {
        console.error('Failed to remove recent item:', error);
      }
    },
    [recentItems]
  );

  const toggleBookmark = useCallback((item: RecentItem) => {
    setBookmarkedItems((prev) => {
      const isBookmarked = prev.some((i) => i.id === item.id);
      const updated = isBookmarked
        ? prev.filter((i) => i.id !== item.id)
        : [item, ...prev.filter((i) => i.id !== item.id)];
      try {
        if (updated.length > 0) {
          userStorage.setItem(BOOKMARK_ITEMS_KEY, JSON.stringify(updated));
        } else {
          userStorage.removeItem(BOOKMARK_ITEMS_KEY);
        }
      } catch (error) {
        console.error('Failed to update bookmarked items:', error);
      }
      return updated;
    });
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearchQuery('');
    setResults({});
    setSelectedIndex(-1);
    setDialogOpen(false);
  }, []);

  const handleFocus = () => {
    if (wrapperRef.current) {
      setAnchorEl(wrapperRef.current);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);

    // Set anchor element if not already set
    if (!anchorEl && event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Clear results if query is too short
    if (query.length < 2) {
      setResults({});
      return;
    }

    // Debounce search API call (350ms after the user stops typing). Scope to the
    // selected group/app; an unset filter means "search everywhere".
    searchTimeoutRef.current = setTimeout(() => {
      triggerSearch({
        q: query,
        limit: 5,
        ...(scopedApplicationGroupId && {
          applicationGroupId: scopedApplicationGroupId,
        }),
        ...(scopedApplicationId && { applicationId: scopedApplicationId }),
      });
    }, 500);
  };

  const handleResultClick = useCallback(
    (
      result: SearchResult,
      navigationContext?: SearchResultNavigationContext
    ) => {
      // Get navigation context from the raw search data if not provided.
      const finalNavigationContext =
        navigationContext ||
        result.navigationContext ||
        (() => {
          const rawResult = searchData?.results
            ? Object.values(searchData.results)
                .flat()
                .find((item) => item.id === result.id)
            : null;
          return (
            getSearchResultNavigationContext(
              rawResult?.navigationContext || rawResult?.metadata
            ) || {}
          );
        })();

      // Save the result to recent items
      saveRecentItem({
        id: result.id,
        title: result.title,
        subtitle: result.subtitle,
        type: result.type as GlobalSearchCategory,
        navigationContext: finalNavigationContext,
        archived: result.archived,
      });

      // Close the search dropdown
      handleClose();
      inputRef.current?.blur();

      const target = buildSearchResultNavigationTarget({
        result,
        navigationContext: finalNavigationContext,
        isUrlNavigationEnabled,
      });

      if (target.path === '#') {
        console.warn('Unknown search result type:', result.type);
        return;
      }

      if (target.groupId && target.applicationId) {
        navigateWithApplicationContext(
          target.groupId,
          target.applicationId,
          target.path,
          { state: target.state }
        );
      } else if (target.groupId) {
        navigateWithGroupContext(target.groupId, target.path, {
          state: target.state,
        });
      } else {
        navigate(target.path, { state: target.state });
      }
    },
    [
      saveRecentItem,
      handleClose,
      searchData,
      navigate,
      navigateWithApplicationContext,
      navigateWithGroupContext,
      isUrlNavigationEnabled,
    ]
  );

  const getResultNavigationContext = useCallback(
    (result: SearchResult, navigationContext?: SearchResultNavigationContext) =>
      navigationContext ||
      result.navigationContext ||
      (() => {
        const rawResult = searchData?.results
          ? Object.values(searchData.results)
              .flat()
              .find((item) => item.id === result.id)
          : null;
        return (
          getSearchResultNavigationContext(
            rawResult?.navigationContext || rawResult?.metadata
          ) || {}
        );
      })(),
    [searchData]
  );

  const getResultPath = useCallback(
    (
      result: SearchResult,
      navigationContext?: SearchResultNavigationContext
    ) => {
      const finalNavigationContext = getResultNavigationContext(
        result,
        navigationContext
      );
      return buildSearchResultNavigationTarget({
        result,
        navigationContext: finalNavigationContext,
        isUrlNavigationEnabled,
      }).path;
    },
    [getResultNavigationContext, isUrlNavigationEnabled]
  );

  const handleRecentItemClick = useCallback(
    (item: RecentItem) => {
      handleResultClick(
        {
          id: item.id,
          title: item.title,
          subtitle: item.subtitle,
          type: item.type,
          archived: item.archived,
        },
        item.navigationContext || item.metadata
      );
    },
    [handleResultClick]
  );

  const shouldLetBrowserHandleLink = (
    event: ReactMouseEvent<HTMLAnchorElement>
  ) =>
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey;

  const handleSearchLinkClick = useCallback(
    (
      event: ReactMouseEvent<HTMLAnchorElement>,
      result: SearchResult,
      navigationContext?: SearchResultNavigationContext
    ) => {
      if (shouldLetBrowserHandleLink(event)) {
        return;
      }

      event.preventDefault();
      handleResultClick(result, navigationContext);
    },
    [handleResultClick]
  );

  const handleBookmarkResult = useCallback(
    (result: SearchResult) => {
      const rawResult = searchData?.results
        ? Object.values(searchData.results)
            .flat()
            .find((item) => item.id === result.id)
        : null;
      const navigationContext =
        result.navigationContext ||
        getSearchResultNavigationContext(
          rawResult?.navigationContext || rawResult?.metadata
        );
      toggleBookmark({
        id: result.id,
        title: result.title,
        subtitle: result.subtitle,
        type: result.type as GlobalSearchCategory,
        navigationContext,
        archived: result.archived,
      });
    },
    [searchData, toggleBookmark]
  );

  const bookmarkedIds = useMemo(
    () => new Set(bookmarkedItems.map((item) => item.id)),
    [bookmarkedItems]
  );

  const recentItemsWithoutBookmarks = useMemo(
    () => recentItems.filter((item) => !bookmarkedIds.has(item.id)),
    [recentItems, bookmarkedIds]
  );

  // When scoped, tier results by proximity to the user's context, bucketed by
  // each result's applicationId/applicationGroupId:
  //   1. In the selected application (only when an app is selected)
  //   2. Elsewhere in the selected group
  //   3. Other groups
  // With no scope (All Groups) it's a single, unlabelled section. Each section
  // caps a category at SECTION_LIMIT.
  const resultSections = useMemo<
    Array<{ key: string; label?: string; groups: GroupedResults }>
  >(() => {
    if (!scopedApplicationGroupId) {
      return Object.keys(results).length > 0
        ? [{ key: 'all', groups: results }]
        : [];
    }

    const inApp: GroupedResults = {};
    const inGroup: GroupedResults = {};
    const otherGroups: GroupedResults = {};
    Object.entries(results).forEach(([groupName, items]) => {
      const appItems: SearchResult[] = [];
      const groupItems: SearchResult[] = [];
      const otherItems: SearchResult[] = [];
      items.forEach((item) => {
        const itemAppId = item.navigationContext?.applicationId;
        const itemGroupId = item.navigationContext?.applicationGroupId;
        if (scopedApplicationId && itemAppId === scopedApplicationId) {
          appItems.push(item);
        } else if (itemGroupId === scopedApplicationGroupId) {
          groupItems.push(item);
        } else {
          otherItems.push(item);
        }
      });
      if (appItems.length) inApp[groupName] = appItems.slice(0, SECTION_LIMIT);
      if (groupItems.length)
        inGroup[groupName] = groupItems.slice(0, SECTION_LIMIT);
      if (otherItems.length)
        otherGroups[groupName] = otherItems.slice(0, SECTION_LIMIT);
    });

    const sections: Array<{
      key: string;
      label?: string;
      groups: GroupedResults;
    }> = [];
    if (scopedApplicationId && Object.keys(inApp).length) {
      sections.push({
        key: 'in-app',
        label: `In ${scopeAppName ?? 'selected application'}`,
        groups: inApp,
      });
    }
    if (Object.keys(inGroup).length) {
      sections.push({
        key: 'in-group',
        label: scopedApplicationId
          ? `Elsewhere in ${scopeGroupName ?? 'group'}`
          : `In ${scopeGroupName ?? 'group'}`,
        groups: inGroup,
      });
    }
    if (Object.keys(otherGroups).length) {
      sections.push({
        key: 'other-groups',
        label: 'Other groups',
        groups: otherGroups,
      });
    }
    return sections;
  }, [
    results,
    scopedApplicationId,
    scopedApplicationGroupId,
    scopeAppName,
    scopeGroupName,
  ]);

  // Get all navigable items (recent items or results)
  const getAllItems = useCallback((): Array<{
    type: 'recent' | 'result';
    data: RecentItem | SearchResult;
  }> => {
    if (showRecents) {
      return [...bookmarkedItems, ...recentItemsWithoutBookmarks].map(
        (item) => ({
          type: 'recent' as const,
          data: item,
        })
      );
    }
    if (showResults && resultSections.length > 0) {
      const items: Array<{ type: 'result'; data: SearchResult }> = [];
      resultSections.forEach((section) => {
        Object.values(section.groups).forEach((groupResults) => {
          groupResults.forEach((result) => {
            items.push({ type: 'result' as const, data: result });
          });
        });
      });
      return items;
    }
    return [];
  }, [
    showRecents,
    showResults,
    resultSections,
    bookmarkedItems,
    recentItemsWithoutBookmarks,
  ]);

  const handleKeyboardNavigation = useCallback(
    (direction: 'up' | 'down') => {
      const items = getAllItems();
      if (items.length === 0) return;

      setSelectedIndex((prevIndex) => {
        let newIndex = prevIndex;
        if (direction === 'down') {
          newIndex = prevIndex < items.length - 1 ? prevIndex + 1 : 0;
        } else {
          newIndex = prevIndex > 0 ? prevIndex - 1 : items.length - 1;
        }

        // Scroll the selected item into view
        setTimeout(() => {
          if (listItemsRef.current[newIndex]) {
            listItemsRef.current[newIndex]?.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth',
            });
          }
        }, 0);

        return newIndex;
      });
    },
    [getAllItems]
  );

  const handleEnterKey = useCallback(() => {
    const items = getAllItems();
    if (selectedIndex >= 0 && selectedIndex < items.length) {
      const item = items[selectedIndex];
      if (item.type === 'recent') {
        handleRecentItemClick(item.data as RecentItem);
      } else {
        handleResultClick(item.data as SearchResult);
      }
    }
  }, [getAllItems, selectedIndex, handleResultClick, handleRecentItemClick]);

  // Update results when search data changes
  useEffect(() => {
    if (searchData?.results) {
      const transformed = transformSearchResults(searchData.results);
      setResults(transformed);
    }
  }, [searchData]);

  useEffect(() => {
    loadRecentItems();
    loadBookmarkedItems();
  }, []);

  useEffect(() => {
    if (dialogOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [dialogOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+K or Cmd+K to focus search
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        if (isSmallScreen) {
          setDialogOpen(true);
        } else {
          inputRef.current?.focus();
        }
      }

      // Escape key to close
      if (event.key === 'Escape' && (anchorEl || dialogOpen)) {
        handleClose();
        inputRef.current?.blur();
      }

      // Arrow keys to navigate when dropdown is open
      if (open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        handleKeyboardNavigation(event.key === 'ArrowDown' ? 'down' : 'up');
      }

      // Enter key to select
      if (event.key === 'Enter' && open && selectedIndex >= 0) {
        event.preventDefault();
        handleEnterKey();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    anchorEl,
    dialogOpen,
    isSmallScreen,
    open,
    selectedIndex,
    handleKeyboardNavigation,
    handleEnterKey,
    handleClose,
  ]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
    listItemsRef.current = [];
  }, [searchQuery, recentItems, bookmarkedItems, results]);

  const searchInput = (
    <TextField
      fullWidth
      size='small'
      inputRef={inputRef}
      placeholder='Search (Ctrl+K)...'
      value={searchQuery}
      onChange={handleSearchChange}
      onFocus={handleFocus}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchTwoToneIcon fontSize='small' />
          </InputAdornment>
        ),
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          backgroundColor: theme.palette.background.paper,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      }}
    />
  );

  const searchResults = (
    <SearchResultsPaper
      sx={{
        width: isSmallScreen ? '100%' : anchorEl?.offsetWidth || 300,
        mt: isSmallScreen ? 0 : 1,
      }}
    >
      {showResults && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 2,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.secondary,
          }}
        >
          {scopeGroupLabel ? (
            <FilterAltTwoToneIcon sx={{ fontSize: 16 }} />
          ) : (
            <PublicTwoToneIcon sx={{ fontSize: 16 }} />
          )}
          <Typography variant='caption' sx={{ fontSize: '12px' }}>
            {scopeGroupLabel ? (
              <>
                Results in <strong>{scopeGroupLabel}</strong>
              </>
            ) : (
              'Results across all groups'
            )}
          </Typography>
        </Box>
      )}

      {showRecents && (
        <List sx={{ py: 1 }}>
          {bookmarkedItems.length > 0 && (
            <Box>
              <GroupHeader>Bookmarks</GroupHeader>
              {bookmarkedItems.map((item, index) => {
                const itemIndex = index;
                return (
                  <ListItem
                    key={item.id}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge='end'
                        size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleBookmark(item);
                        }}
                        sx={{
                          opacity: 0.7,
                          '&:hover': {
                            opacity: 1,
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <BookmarkIcon fontSize='small' />
                      </IconButton>
                    }
                  >
                    <ListItemButton
                      component={RouterLink}
                      state={{ breadcrumbName: item.title }}
                      to={getResultPath(
                        {
                          id: item.id,
                          title: item.title,
                          subtitle: item.subtitle,
                          type: item.type,
                        },
                        item.navigationContext || item.metadata
                      )}
                      ref={(el) => (listItemsRef.current[itemIndex] = el)}
                      onClick={(event) =>
                        handleSearchLinkClick(
                          event,
                          {
                            id: item.id,
                            title: item.title,
                            subtitle: item.subtitle,
                            type: item.type,
                          },
                          item.navigationContext || item.metadata
                        )
                      }
                      selected={selectedIndex === itemIndex}
                      sx={{
                        pl: 3,
                        pr: 6,
                        color: 'inherit',
                        textDecoration: 'none',
                        '&.Mui-selected': {
                          color: theme.tokens.colors.activeItemForeground,
                          backgroundColor: theme.tokens.colors.activeItem,
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: theme.tokens.colors.activeItem,
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {item.title}
                            {item.archived && (
                              <Chip
                                label='Archived'
                                size='small'
                                color='primary'
                                variant='outlined'
                                sx={{ height: 18, fontSize: '10px' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={item.subtitle}
                        primaryTypographyProps={{
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                        secondaryTypographyProps={{
                          fontSize: '12px',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </Box>
          )}

          {recentItemsWithoutBookmarks.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <GroupHeader sx={{ p: 0, backgroundColor: 'transparent' }}>
                  Recent Items
                </GroupHeader>
                <Button
                  size='small'
                  onClick={clearRecentItems}
                  sx={{
                    fontSize: '11px',
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 1,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      color: theme.palette.primary.main,
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Clear
                </Button>
              </Box>
              {recentItemsWithoutBookmarks.map((item, index) => {
                const itemIndex = bookmarkedItems.length + index;
                return (
                  <ListItem
                    key={item.id}
                    disablePadding
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                          edge='end'
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleBookmark(item);
                          }}
                          sx={{
                            opacity: 0.7,
                            '&:hover': {
                              opacity: 1,
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          <BookmarkBorderIcon fontSize='small' />
                        </IconButton>
                        <IconButton
                          edge='end'
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation();
                            removeRecentItem(item.id);
                          }}
                          sx={{
                            opacity: 0.6,
                            '&:hover': {
                              opacity: 1,
                              color: theme.palette.error.main,
                            },
                          }}
                        >
                          <CloseTwoToneIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemButton
                      component={RouterLink}
                      state={{ breadcrumbName: item.title }}
                      to={getResultPath(
                        {
                          id: item.id,
                          title: item.title,
                          subtitle: item.subtitle,
                          type: item.type,
                        },
                        item.navigationContext || item.metadata
                      )}
                      ref={(el) => (listItemsRef.current[itemIndex] = el)}
                      onClick={(event) =>
                        handleSearchLinkClick(
                          event,
                          {
                            id: item.id,
                            title: item.title,
                            subtitle: item.subtitle,
                            type: item.type,
                          },
                          item.navigationContext || item.metadata
                        )
                      }
                      selected={selectedIndex === itemIndex}
                      sx={{
                        pl: 3,
                        pr: 8,
                        color: 'inherit',
                        textDecoration: 'none',
                        '&.Mui-selected': {
                          color: theme.tokens.colors.activeItemForeground,
                          backgroundColor: theme.tokens.colors.activeItem,
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: theme.tokens.colors.activeItem,
                        },
                      }}
                    >
                      <HistoryTwoToneIcon
                        fontSize='small'
                        sx={{ mr: 1.5, color: theme.palette.text.secondary }}
                      />
                      <ListItemText
                        primary={item.title}
                        secondary={item.subtitle}
                        primaryTypographyProps={{
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                        secondaryTypographyProps={{
                          fontSize: '12px',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </Box>
          )}
        </List>
      )}

      {showResults && searchQuery.length > 0 && searchQuery.length < 2 && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant='body2' color='text.secondary'>
            Type at least 2 characters to search
          </Typography>
        </Box>
      )}

      {showResults && searchQuery.length >= 2 && isFetching && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress size={24} />
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            Searching...
          </Typography>
        </Box>
      )}

      {showResults &&
        searchQuery.length >= 2 &&
        !isFetching &&
        Object.keys(results).length === 0 &&
        !isError && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              No results found for "{searchQuery}"
            </Typography>
          </Box>
        )}

      {showResults && searchQuery.length >= 2 && isError && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant='body2' color='error.main'>
            Error performing search. Please try again.
          </Typography>
        </Box>
      )}

      {showResults && resultSections.length > 0 && !isFetching && (
        <List sx={{ py: 1 }}>
          {(() => {
            let currentItemIndex = 0;
            return resultSections.map((section, sectionIndex) => (
              <Box key={section.key}>
                {section.label && (
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: theme.palette.text.secondary,
                      backgroundColor: theme.palette.background.default,
                      px: 2,
                      py: 1,
                      ...(sectionIndex > 0 && {
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }),
                    }}
                  >
                    {section.label}
                  </Typography>
                )}
                {Object.entries(section.groups).map(
                  ([groupName, groupResults]) => {
                    const category = groupResults[0]?.type;
                    return (
                      <Box key={`${section.key}-${groupName}`}>
                        <GroupHeader
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          {category && (
                            <EntityIcon type={category} sx={{ fontSize: 16 }} />
                          )}
                          {groupName}
                        </GroupHeader>
                        {groupResults.map((result) => {
                          const itemIndex = currentItemIndex++;
                          const isSelected = selectedIndex === itemIndex;
                          const isBookmarked = bookmarkedIds.has(result.id);
                          return (
                            <ListItem
                              key={result.id}
                              disablePadding
                              className={
                                isSelected ? 'keyboard-selected' : undefined
                              }
                              secondaryAction={
                                <IconButton
                                  className='search-result-bookmark'
                                  edge='end'
                                  size='small'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBookmarkResult(result);
                                  }}
                                  sx={{
                                    opacity: 0,
                                    transition: 'opacity 120ms ease',
                                    '&:hover': {
                                      color: theme.palette.primary.main,
                                    },
                                  }}
                                >
                                  {isBookmarked ? (
                                    <BookmarkIcon fontSize='small' />
                                  ) : (
                                    <BookmarkBorderIcon fontSize='small' />
                                  )}
                                </IconButton>
                              }
                              sx={{
                                '&:hover .search-result-bookmark': {
                                  opacity: 0.9,
                                },
                                '&.keyboard-selected .search-result-bookmark': {
                                  opacity: 0.9,
                                },
                              }}
                            >
                              <ListItemButton
                                component={RouterLink}
                                state={{ breadcrumbName: result.title }}
                                to={getResultPath(result)}
                                ref={(el) =>
                                  (listItemsRef.current[itemIndex] = el)
                                }
                                onClick={(event) =>
                                  handleSearchLinkClick(event, result)
                                }
                                selected={isSelected}
                                sx={{
                                  pl: 3,
                                  py: 0,
                                  pr: 6,
                                  color: 'inherit',
                                  textDecoration: 'none',
                                  '&.Mui-selected': {
                                    color:
                                      theme.tokens.colors.activeItemForeground,
                                    backgroundColor:
                                      theme.tokens.colors.activeItem,
                                  },
                                  '&.Mui-selected:hover': {
                                    backgroundColor:
                                      theme.tokens.colors.activeItem,
                                  },
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      {result.title}
                                      {result.archived && (
                                        <Chip
                                          label='Archived'
                                          size='small'
                                          color='primary'
                                          variant='outlined'
                                          sx={{ height: 18, fontSize: '10px' }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  secondary={result.subtitle}
                                  primaryTypographyProps={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                  }}
                                  secondaryTypographyProps={{
                                    fontSize: '12px',
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </Box>
                    );
                  }
                )}
              </Box>
            ));
          })()}
        </List>
      )}
    </SearchResultsPaper>
  );

  return isSmallScreen ? (
    <Box sx={{ position: 'relative', mr: 1 }} ref={wrapperRef}>
      <Button
        size='small'
        variant='outlined'
        onClick={() => setDialogOpen(true)}
        startIcon={<SearchTwoToneIcon fontSize='small' />}
        sx={{ textTransform: 'none' }}
      >
        Search
      </Button>
      <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth='sm'>
        <Box sx={{ p: 2, pb: 0 }}>{searchInput}</Box>
        <Box sx={{ px: 1, pb: 1 }}>{searchResults}</Box>
      </Dialog>
    </Box>
  ) : (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: 'relative', width: 400, mr: 1 }} ref={wrapperRef}>
        {searchInput}

        <Popper
          open={open}
          anchorEl={anchorEl}
          placement='bottom-start'
          style={{ zIndex: 1300 }}
        >
          {searchResults}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default Search;
